import React from 'react';
import { Dialog, DialogContent } from '@/ui-components/ui/dialog';
import { Button } from '@/ui-components/ui/button';
import { Trash2, Download } from 'lucide-react';
import { Permission } from "@/lib/api/interfaces/permissions.interface";
import { PlanKey } from "@/lib/api/interfaces/billing.interface";
import { PermissionFeatureWrapper } from "@/util-components/auth/PermissionFeatureWrapper";
import { MediaLibraryItemDto } from '@/lib/api/interfaces/media-library.interface';
import { formatFileSize, getFileIcon } from './utils';

interface MediaPreviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: MediaLibraryItemDto;
  onDelete: (mediaId: string) => void;
  onDownload: (mediaId: string) => void;
  onEdit: (mediaId: string) => void;
}

/**
 * Modern media preview component inspired by Figma and Trade Republic
 * Features clean, minimal design with compact action buttons
 */
export const MediaPreview: React.FC<MediaPreviewProps> = ({
  open,
  onOpenChange,
  item,
  onDelete,
  onDownload,
  onEdit
}) => {
  
  const isSvg = item.mimeType.includes('svg');
  const isImage = item.mimeType.startsWith('image/') || isSvg;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} >
      <DialogContent className="max-w-4xl w-full h-[90vh] p-0 bg-white border-0 shadow-2xl rounded-xl overflow-hidden flex flex-col gap-0">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-white flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 rounded-md bg-gray-50 flex items-center justify-center">
                {getFileIcon(item.mimeType, 'w-4 h-4 text-gray-600')}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-900 truncate max-w-xs" title={item.displayName}>
                {item.displayName}
              </h3>
              <p className="text-xs text-gray-500">
                {formatFileSize(item.size)} • {item.mimeType}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 relative overflow-hidden min-h-0 bg-gray-50">
          {isImage ? (
            <div className={`absolute inset-4 flex items-center justify-center ${isSvg ? 'p-8' : ''}`}>
              <img
                src={item.url}
                alt={item.displayName}
                className={`max-w-full max-h-full ${isSvg ? 'object-contain' : 'object-contain'} rounded-lg`}
                style={{ 
                  filter: 'drop-shadow(0 20px 40px rgba(0, 0, 0, 0.12))'
                }}
              />
            </div>
          ) : (
            <div className="text-center">
              <div className="w-24 h-24 mx-auto mb-4 rounded-2xl bg-white shadow-sm flex items-center justify-center">
                {getFileIcon(item.mimeType, 'w-12 h-12 text-gray-400')}
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-2">
                {item.displayName}
              </h4>
              <p className="text-sm text-gray-500 mb-4">
                Diese Datei kann nicht in der Vorschau angezeigt werden
              </p>
              <Button
                onClick={() => onDownload(item.mediaId)}
                className="bg-gray-900 hover:bg-gray-800 text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                Datei herunterladen
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-white flex-shrink-0">
          {/* Action Buttons */}
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDownload(item.mediaId)}
              className="h-8 px-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50"
            >
              <Download className="w-4 h-4 mr-1.5" />
              Download
            </Button>
            <PermissionFeatureWrapper
              requiredPermission={Permission.MANAGE_ENTITIES}
              requiredPlan={PlanKey.BASIC}
            >
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(item.mediaId)}
                className="h-8 px-3 text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-1.5" />
                Löschen
              </Button>
            </PermissionFeatureWrapper>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};